import { Drawer, Spin, theme } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CheckBeforeJoin from '../HOC/CheckBeforeJoin';
import RecordList from './RecordList';
import HistoryTime from './HistoryTime';
import DoingTime from './DoingTime';
import Summary from './Summary';
import ParticipantList from './ParticipantList';
import { TMeeting } from '@/common/basis/types/database/tmeeting';
import { TMeetingRecord } from '@/common/basis/types/database/tmeeting-record';
import { TPerson } from '@/common/basis/types/database/tperson';
import { isSuccessCode } from '@/common/utils/response';
import { getMeetingDetail, getMeetingJoinRecordList } from '@/common/http/meet';
import { TField } from '@/common/basis/types/tfield';
import { getUserAvatar } from '@/common/utils/image';
import { getUserInfo } from '@/common/http/user';
import { setMeetingShareData } from '@/common/store/popup/slice';
import { useMeetingType } from '@/common/hooks/meeting-type';
import { useMeetingBrightPassword } from '@/common/hooks/meeting-bright-password';
import { joinParameters } from '@/common/utils/uri';

function App(props: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  roomId: string;
  roomSid: string;
  children?: React.ReactNode;
}) {
  const dispatch = useDispatch();
  const isOpen = props.isOpen;
  const setIsOpen = props.setIsOpen;
  const roomId = props.roomId;
  const roomSid = props.roomSid;

  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detail, setDetail] = useState<TMeeting.Data>();
  const [hostInfo, setHostInfo] = useState<TPerson.User>();
  const [recordList, setRecordList] = useState<TMeetingRecord.Item[]>();
  const themeToken = theme.useToken();
  const { t } = useTranslation();

  const meetingType = useMemo(() => detail?.meetingType ?? TField.MEETING_TYPE_BOOKING, [detail?.meetingType]);
  const { isMeetingLive } = useMeetingType(meetingType);
  const liveUrl = useMemo(() => (isMeetingLive ? (detail?.liveUrl ?? '') : ''), [detail?.liveUrl, isMeetingLive]);
  const roomName = useMemo(() => detail?.roomName ?? '', [detail?.roomName]);
  const startedTime = useMemo(() => detail?.startedTime ?? 0, [detail?.startedTime]);
  const finishedTime = useMemo(() => detail?.finishedTime ?? 0, [detail?.finishedTime]);
  const isHistoryMeeting = useMemo(() => finishedTime > 0, [finishedTime]);
  const isMeetingDoing = useMemo(() => detail?.status === TField.ROOM_MEET_STATUS_DOING, [detail?.status]); // 会议进行中

  // 进行中会议 仅展示 当前参会人 不展示已离开参会人
  const participants = useMemo(
    () =>
      detail?.participants?.filter(
        (value) => value?.hidden !== true && (isMeetingDoing ? value?.status === TField.USER_MEET_STATUS_2 : true),
      ) ?? [],
    [detail?.participants, isMeetingDoing],
  ); // 过滤隐藏用户 例如: 录制、字幕机器人等
  const participantsLength = useMemo(() => participants.length, [participants]);
  const password = useMemo(() => detail?.password || '', [detail?.password]);
  const nonce = useMemo(() => detail?.nonce || '', [detail?.nonce]);
  const webUrl = useMemo(() => detail?.webUrl || '', [detail?.webUrl]);
  const { brightPassword } = useMeetingBrightPassword(password, nonce);

  // 获取相关会议详情数据
  const requestMeetingData = async () => {
    if (isDetailLoading) {
      return;
    }

    setIsDetailLoading(true);
    const [detailResp, recordResp] = await Promise.all([
      getMeetingDetail({
        roomId,
        roomSid,
        haveParticipant: true,
        range: TField.MEET_RANGE_REALITY_JOIN,
      }), // 获取会议详情
      getMeetingJoinRecordList({ roomId, roomSid }), // 获取会议记录
    ]);

    if (isSuccessCode(detailResp?.code)) {
      const data = detailResp.data;
      const defaultHost = data?.defaultHost;
      const defaultHostInfo = data?.participants?.find((item) => item?.userId === defaultHost);

      // 未在参与者中找到当前默认主持人时（预约会议的人未入会时，参与者中找不到）
      if (defaultHostInfo) {
        setHostInfo(defaultHostInfo);
      } else if (defaultHost) {
        const resp = await getUserInfo({ userId: defaultHost });

        if (isSuccessCode(resp?.code)) {
          setHostInfo(resp.data);
        }
      }
      setDetail(data);
    }
    if (isSuccessCode(recordResp?.code)) {
      setRecordList(recordResp.data.list);
    }
    setIsDetailLoading(false);
  };

  useEffect(() => {
    if (isOpen && roomId && roomSid) {
      requestMeetingData();
    }

    return () => {
      setDetail(undefined);
      setHostInfo(undefined);
      setRecordList([]);
    };
  }, [isOpen, roomId, roomSid]);

  return (
    <>
      <Drawer
        destroyOnHidden
        onClose={() => setIsOpen(false)}
        open={isOpen}
        styles={{
          header: { paddingTop: 28 },
          mask: { opacity: 0 },
          content: { backgroundColor: '#f2f3f5' },
          body: { padding: 0, margin: 0 },
        }}
        title={
          <div className='relative flex items-center justify-center'>
            <span>{isHistoryMeeting ? t('historyMeetingDetails') : t('meetingDetails')}</span>
          </div>
        }
        width={390}
      >
        {isDetailLoading ? (
          <div className='flex h-full justify-center pt-10'>
            <Spin />
          </div>
        ) : (
          <div className='relative flex h-full flex-col'>
            <div className='flex-1 overflow-y-auto'>
              <div className='overflow-hidden p-4'>
                <div className='rounded-xl bg-[#fff] p-3 py-4'>
                  <span className='break-words text-[16px] font-medium'>{roomName}</span>
                </div>
                <div className='my-4 rounded-xl bg-[#fff] py-4 text-[14px] text-[rgb(10,10,23)]'>
                  <div className='flex h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('meetingId')}</span>
                    </div>
                    <div className='font-medium'>
                      <span>{roomId}</span>
                    </div>
                  </div>
                  <div className='h-[1px] bg-[rgb(229,229,230)]' />
                  <div className='flex min-h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('meetingTime')}</span>
                    </div>
                    <div>
                      {isHistoryMeeting ? (
                        <HistoryTime finishedTime={finishedTime} startedTime={startedTime} />
                      ) : (
                        <DoingTime startedTime={startedTime} />
                      )}
                    </div>
                  </div>

                  <div className='h-[1px] bg-[rgb(229,229,230)]' />
                  <div className='flex min-h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('promoter')}</span>
                    </div>
                    <div className='py-2'>
                      <div className='h-12 w-12 overflow-hidden rounded-lg'>
                        <img className='h-full w-full object-cover' src={getUserAvatar(hostInfo?.avatar ?? '')} />
                      </div>
                      <div className='mt-1 text-center'>
                        <span>{hostInfo?.name ?? hostInfo?.userName}</span>
                      </div>
                    </div>
                  </div>
                  <div className='h-[1px] bg-[rgb(229,229,230)]' />
                  <div className='min-h-12 overflow-hidden px-2'>
                    <div className='mt-3 px-1'>
                      <span>
                        {t('participants')}
                        {participantsLength ? `(${participantsLength})` : ''}
                      </span>
                    </div>
                    <div className='flex flex-wrap items-center py-3'>
                      <ParticipantList list={participants} />
                    </div>
                  </div>
                </div>
                <Summary item={detail} setIsOpen={setIsOpen} />
                <RecordList recordList={recordList} />
                {props.children}
              </div>
            </div>
            {isHistoryMeeting ? (
              <></>
            ) : (
              <div className='flex h-14 items-center justify-center border-0 border-t-[1px] border-solid border-[rgba(19,20,21,.12)] bg-[#f7f7f7]'>
                <div
                  className='flex h-full flex-1 cursor-pointer items-center justify-center text-[14px] font-medium'
                  onClick={() => {
                    dispatch(setMeetingShareData({ roomId, roomName, brightPassword, webUrl, liveUrl }));
                  }}
                >
                  <span style={{ color: themeToken.token.colorPrimary }}>{t('invite')}</span>
                </div>
                <div className='mx-1 h-6 w-[1px] rounded bg-[rgba(19,20,21,.12)]' />
                <div className='relative flex h-full flex-1 cursor-pointer items-center justify-center text-[14px] font-medium'>
                  <CheckBeforeJoin
                    callback={(success) => {
                      if (success) {
                        setIsOpen(false);
                        window.open(joinParameters(webUrl, { showSchema: 0 }));
                      }
                    }}
                    roomId={roomId}
                  >
                    <span style={{ color: themeToken.token.colorPrimary }}>{t('enterMeeting')}</span>
                  </CheckBeforeJoin>
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}

export default App;
